import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { duration as motionDuration } from '@digiwedge/ui-tokens';
import { useReducedMotion } from '@digiwedge/ui-headless';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon, } from '@heroicons/react/24/outline';
import { TEE_TIME_OVERLAY_LAYER } from '../../utils/overlay-layers.js';
const toastStyles = {
    info: {
        container: 'bg-white border-info/30',
        icon: 'text-info',
        Icon: InformationCircleIcon,
    },
    success: {
        container: 'bg-white border-success/30',
        icon: 'text-success',
        Icon: CheckCircleIcon,
    },
    warning: {
        container: 'bg-white border-warning/30',
        icon: 'text-warning',
        Icon: ExclamationTriangleIcon,
    },
    error: {
        container: 'bg-white border-error/30',
        icon: 'text-error',
        Icon: XCircleIcon,
    },
};
/**
 * A toast notification component for temporary messages.
 *
 * @example
 * <Toast
 *   type="success"
 *   title="Saved!"
 *   message="Your changes have been saved."
 *   duration={3000}
 *   onDismiss={() => setShow(false)}
 * />
 */
export const Toast = ({ type = 'info', title, message, duration = 5000, dismissible = true, onDismiss, className, action, }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);
    const prefersReducedMotion = useReducedMotion();
    const closeDelayMs = prefersReducedMotion ? 0 : motionDuration.normal;
    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            setIsVisible(false);
            onDismiss?.();
        }, closeDelayMs);
    }, [closeDelayMs, onDismiss]);
    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(handleDismiss, duration);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [duration, handleDismiss]);
    if (!isVisible) {
        return null;
    }
    const styles = toastStyles[type];
    const IconComponent = styles.Icon;
    return (_jsx("div", { role: "alert", "aria-live": "polite", className: twMerge('pointer-events-auto w-full max-w-sm rounded-lg border shadow-lg transition-all duration-[var(--animation-duration)]', styles.container, isExiting ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0', className), children: _jsx("div", { className: "p-4", children: _jsxs("div", { className: "flex items-start", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx(IconComponent, { className: twMerge('h-5 w-5', styles.icon), "aria-hidden": "true" }) }), _jsxs("div", { className: "ml-3 flex-1", children: [title && _jsx("p", { className: "text-sm font-medium text-gray-900", children: title }), _jsx("p", { className: twMerge('text-sm text-gray-600', title && 'mt-1'), children: message }), action && (_jsx("div", { className: "mt-2", children: _jsx("button", { type: "button", onClick: action.onClick, className: "text-sm font-medium text-primary hover:text-primary/80 focus:outline-none", children: action.label }) }))] }), dismissible && (_jsx("div", { className: "ml-4 flex-shrink-0", children: _jsxs("button", { type: "button", onClick: handleDismiss, className: "inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2", children: [_jsx("span", { className: "sr-only", children: "Dismiss" }), _jsx(XMarkIcon, { className: "h-5 w-5", "aria-hidden": "true" })] }) }))] }) }) }));
};
Toast.displayName = 'Toast';
const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};
/**
 * Container for positioning toasts on the screen.
 *
 * @example
 * <ToastContainer position="top-right">
 *   {toasts.map(toast => <Toast key={toast.id} {...toast} />)}
 * </ToastContainer>
 */
export const ToastContainer = ({ position = 'top-right', className, children, }) => {
    return (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", className: twMerge(`pointer-events-none fixed ${TEE_TIME_OVERLAY_LAYER.notification.className} flex flex-col gap-2`, positionClasses[position], className), children: children }));
};
ToastContainer.displayName = 'ToastContainer';
//# sourceMappingURL=Toast.js.map